let mixitup = require('mixitup');
import Swiper, { Mousewheel, Navigation, Pagination } from 'swiper';

/**
 * Filter class provides functionality for filtering and comparing table cards.
 * @class Filter
 */
class Filter {
    /**
     * Container element for the entire filter module.
     * @type {HTMLElement|null}
     */
    container;

    /**
     * Container element for the filterable cards.
     * @type {HTMLElement|null}
     */
    cardsContainer;

    /**
     * Instance of MixItUp for filtering.
     * @type {Object|null}
     */
    mixer;

    /**
     * Array of filter button elements.
     * @type {Array<HTMLElement>}
     */
    filterButtons = [];

    /**
     * Button to clear active filters.
     * @type {HTMLElement|null}
     */
    clearButton;

    /**
     * Array of checkbox input elements.
     * @type {Array<HTMLElement>}
     */
    checkboxes = [];

    /**
     * Object to store checked card references keyed by ID.
     * @type {Object<string, HTMLElement>}
     */
    checkedCards = {};

    /**
     * Array of all card elements.
     * @type {Array<HTMLElement>}
     */
    cards = [];

    /**
     * Footer bar element for comparison functionality.
     * @type {HTMLElement|null}
     */
    footerBar;

    /**
     * Button to collapse/expand the footer bar.
     * @type {HTMLElement|null}
     */
    footerBarCollapseButton;

    /**
     * Button to cancel comparison in footer bar.
     * @type {HTMLElement|null}
     */
    footerBarCancelButton;

    /**
     * Button to initiate comparison.
     * @type {HTMLElement|null}
     */
    footerBarCompareButton;

    /**
     * Container for cards in the footer bar.
     * @type {HTMLElement|null}
     */
    footerBarCardsContainer;

    /**
     * Repeater element for footer bar cards.
     * @type {HTMLElement|null}
     */
    footerBarCardsRepeater;

    /**
     * Template for footer bar card.
     * @type {HTMLTemplateElement|null}
     */
    footerBarCardTemplate;

    /**
     * Container for comparison screen.
     * @type {HTMLElement|null}
     */
    comparisonsScreen;

    /**
     * Repeater element for comparison screen cards.
     * @type {HTMLElement|null}
     */
    comparisonsScreenCardsRepeater;

    /**
     * Button to clear comparisons.
     * @type {HTMLElement|null}
     */
    comparisonsClearButton;

    /**
     * Swiper instance for comparison screen slider.
     * @type {Swiper|null}
     */
    comparisonsScreenSlider;

    /**
     * Element for header slider.
     * @type {HTMLElement|null}
     */
    headerSliderElement;

    /**
     * Swiper instance for header slider.
     * @type {Swiper|null}
     */
    headerSlider;

    /**
     * Swiper instance for footer bar slider.
     * @type {Swiper|null}
     */
    footerBarSlider;

    /**
     * Element for stats count.
     * @type {HTMLElement|null}
     */
    statsCountElement;

    /**
     * Initialize the Filter instance and set up event handlers.
     * @param {HTMLElement} block - The container element for the filter module.
     */
    constructor(block) {
        if (!block) {
            return;
        }
        this.container = block;
        this.cardsContainer = block.querySelector('.js-cards-container');
        this.filterButtons = Array.from(
            block.querySelectorAll('.js-filter-btn'),
        );
        this.clearButton = block.querySelector('.js-clear-filter');
        this.checkboxes = Array.from(block.querySelectorAll('.js-table-input'));
        this.cards = Array.from(block.querySelectorAll('.js-card'));
        this.footerBar = block.querySelector('.js-footer-bar');
        this.footerBarCardsContainer = block.querySelector(
            '.js-footer-bar-cards-container',
        );
        this.footerBarCardsRepeater = block.querySelector(
            '.js-footer-bar-cards-repeater',
        );
        this.footerBarCollapseButton = block.querySelector(
            '.js-footer-collapse-btn',
        );
        this.footerBarCompareButton = block.querySelector(
            '.js-footer-compare-plans-btn',
        );
        this.footerBarCancelButton = block.querySelector(
            '.js-cancel-compare-btn',
        );
        this.footerBarCardTemplate = block.querySelector(
            'template.js-footer-bar-card-template',
        );
        this.comparisonsScreen = block.querySelector('.js-comparisons-screen');
        this.comparisonsScreenCardsRepeater = block.querySelector(
            '.js-cs-cards-repeater',
        );
        this.comparisonsClearButton = block.querySelector(
            '.js-cs-clear-button',
        );
        this.headerSliderElement = block.querySelector('.js-header-slider');
        this.statsCountElement = block.querySelector('.js-stats-count');

        // Register Swiper modules.
        Swiper.use([Mousewheel, Navigation, Pagination]);

        // Initialize core functionality.
        this.updateCardsHeight();
        this.initMixer();
        this.initHeaderSlider();
        this.bindEventListeners();

        // TODO:
        // - Update slider on window resize
        // - Hide expanded cards on cancel button click (footer)
        // - Hide banner if no cards selected on the remove card button
    }

    /**
     * Set the same height for the cards.
     */
    updateCardsHeight() {
        if (!this.cards.length) {
            return;
        }
        const maxHeight = Math.max(
            ...this.cards.map((card) => card.offsetHeight),
        );

        if (maxHeight > 0) {
            this.cards.forEach((card) => {
                card.style.height = `${maxHeight}px`;
            });
        }
    }

    /**
     * Bind all event listeners for Filter functionality.
     * @private
     */
    bindEventListeners() {
        // Attach filter button click handlers
        if (this.filterButtons.length) {
            this.filterButtons.forEach((button) => {
                button.addEventListener(
                    'click',
                    this.filterBtnClickHandler.bind(this),
                );
            });
        }

        // Attach clear button handler
        if (this.clearButton) {
            this.clearButton.addEventListener(
                'click',
                this.clearButtonClickHandler.bind(this),
            );
        }

        // Attach checkbox change handlers
        if (this.checkboxes.length) {
            this.checkboxes.forEach((checkbox) => {
                checkbox.addEventListener(
                    'change',
                    this.checkboxChangeHandler.bind(this),
                );
            });
        }

        // Attach footer bar button handlers
        if (this.footerBarCollapseButton) {
            this.footerBarCollapseButton.addEventListener(
                'click',
                this.footerBarCollapseButtonClickHandler.bind(this),
            );
        }

        if (this.footerBarCancelButton) {
            this.footerBarCancelButton.addEventListener(
                'click',
                this.footerBarCancelClickHandler.bind(this),
            );
        }

        if (this.footerBarCompareButton) {
            this.footerBarCompareButton.addEventListener(
                'click',
                this.comparePlansClickHandler.bind(this),
            );
        }

        // Attach comparison clear button handler
        if (this.comparisonsClearButton) {
            this.comparisonsClearButton.addEventListener(
                'click',
                this.comparisonsClearButtonClickHandler.bind(this),
            );
        }

        // Attach footer card removal handlers
        if (this.footerBarCardsRepeater) {
            this.footerBarCardsRepeater.addEventListener(
                'click',
                this.handleFooterCardRemove.bind(this),
            );
        }

        // Debounce
        this.debouncedResizeHandler = this.debounce(
            this.handleWindowResize.bind(this),
            150,
        );

        // Add window resize handler for responsive behavior
        window.addEventListener('resize', this.debouncedResizeHandler);
    }

    /**
     * Debounce helper to limit function execution rate.
     * @param {Function} func - Function to debounce.
     * @param {number} wait - Wait time in milliseconds.
     * @returns {Function}
     */
    debounce(func, wait) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    /**
     * Handle window resize events.
     * @private
     */
    handleWindowResize() {
        // Reinitialize header slider on resize
        this.initHeaderSlider();

        // Update cards heights
        this.updateCardsHeight();

        // Update any existing sliders
        if (this.footerBarSlider) {
            this.footerBarSlider.update();
        }

        if (this.comparisonsScreenSlider) {
            this.comparisonsScreenSlider.update();
        }
    }

    /**
     * Handle footer card removal.
     * @param {Event} event - Click event.
     * @private
     */
    handleFooterCardRemove(event) {
        const removeBtn = event.target.closest('.js-footer-card-remove');
        if (!removeBtn) {
            return;
        }

        const card = event.target.closest('.js-footer-card');
        if (!card) {
            return;
        }

        event.preventDefault();

        const id = card.dataset && card.dataset.id ? card.dataset.id : '';
        const input = this.cardsContainer.querySelector(
            `input.js-table-input[value="${id}"]`,
        );

        if (input) {
            input.checked = false;
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
        }
    }

    /**
     * Initialize the MixItUp filtering library.
     * @private
     */
    initMixer() {
        if (!this.cardsContainer) {
            return;
        }

        this.mixer = mixitup(this.cardsContainer, {
            animation: {
                duration: 400,
                effectsIn: 'fade',
                effectsOut: 'fade',
                easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
            },
            load: {
                filter: 'all',
            },
            callbacks: {
                onMixEnd: ({ totalShow }) => {
                    if (this.statsCountElement) {
                        this.statsCountElement.innerText = `${totalShow} ${
                            totalShow === 1 ? 'plan' : 'plans'
                        }`;
                    }
                },
            },
        });
    }

    /**
     * Initialize the header slider based on current viewport width.
     * @private
     */
    initHeaderSlider() {
        if (!this.headerSliderElement) {
            return;
        }

        // Create slider on mobile and destroy on desktop
        if (window.innerWidth < 1024) {
            // If slider already exists, destroy it before recreating
            if (
                this.headerSlider &&
                typeof this.headerSlider.destroy === 'function'
            ) {
                this.headerSlider.destroy(true, true);
            }

            this.headerSlider = new Swiper(this.headerSliderElement, {
                slidesPerView: 'auto',
                spaceBetween: 10,
                enabled: true,
                pagination: {
                    el: this.headerSliderElement.querySelector(
                        '.js-pagination-progressbar',
                    ),
                    type: 'progressbar',
                },
                navigation: {
                    nextEl: this.headerSliderElement.querySelector(
                        '.js-navigation .swiper-button-next',
                    ),
                    prevEl: this.headerSliderElement.querySelector(
                        '.js-navigation .swiper-button-prev',
                    ),
                },
                slidesOffsetBefore: window.innerWidth * 0.05 + 15,
                slidesOffsetAfter: window.innerWidth * 0.05 + 15,
                breakpoints: {
                    1024: {
                        enabled: false,
                    },
                },
            });
        } else if (
            this.headerSlider &&
            typeof this.headerSlider.destroy === 'function'
        ) {
            this.headerSlider.destroy(true, true);
            this.headerSlider = null;
        }
    }

    /**
     * Handle filter button clicks.
     * @param {Event} event - Click event.
     * @private
     */
    filterBtnClickHandler(event) {
        event.preventDefault();

        if (!this.mixer) {
            return;
        }

        const button = event.target.closest('.js-filter-btn');
        if (!button || !button.dataset) {
            return;
        }

        const filterId = button.dataset.value || '0';
        this.mixer.filter(`.filter-id-${filterId}`);

        this.deselectAllFiltersButtons();
        button.classList.add('active');
    }

    /**
     * Handle clear button clicks.
     * @param {Event} event - Click event.
     * @private
     */
    clearButtonClickHandler(event) {
        event.preventDefault();

        if (!this.mixer) {
            return;
        }

        this.mixer.filter('all');
        this.deselectAllFiltersButtons();
    }

    /**
     * Remove active class from all filter buttons.
     * @private
     */
    deselectAllFiltersButtons() {
        this.filterButtons.forEach((button) => {
            button.classList.remove('active');
        });
    }

    /**
     * Handle checkbox state changes.
     * @param {Event} event - Change event.
     * @private
     */
    checkboxChangeHandler(event) {
        const input = event.target;
        const card = input.closest('.js-card');
        if (!card) {
            return;
        }

        const id = Number(input.value);
        if (isNaN(id)) {
            return;
        }

        if (input.checked) {
            this.checkedCards[id] = card;
            this.footerBarAddCard(card);
        } else {
            if (this.checkedCards[id]) {
                delete this.checkedCards[id];
            }
            this.footerBarRemoveCard(id);
        }

        this.toggleFooterBar(Object.keys(this.checkedCards).length > 0);
    }

    /**
     * Show or hide the footer bar based on condition.
     * @param {boolean} shouldShow - Whether to show or hide the footer bar.
     * @private
     */
    toggleFooterBar(shouldShow = true) {
        if (shouldShow) {
            this.showFooterBar();
        } else {
            this.hideFooterBar();
        }
    }

    /**
     * Remove a card from the footer bar by ID.
     * @param {number} id - Card ID.
     * @private
     */
    footerBarRemoveCard(id) {
        if (!this.footerBarCardsRepeater) {
            return;
        }

        const card = this.footerBarCardsRepeater.querySelector(
            `.js-footer-card[data-id="${id}"]`,
        );
        if (!card) {
            return;
        }

        jQuery(card).fadeOut(400, () => {
            if (!this.footerBarSlider) {
                card.remove();
            } else {
                let slideIndex;
                for (let i = 0; i < this.footerBarSlider.slides.length; i++) {
                    const slideId =
                        this.footerBarSlider.slides[i] &&
                        this.footerBarSlider.slides[i].dataset &&
                        this.footerBarSlider.slides[i].dataset.id
                            ? Number(this.footerBarSlider.slides[i].dataset.id)
                            : null;
                    if (slideId === id) {
                        slideIndex = i;
                        break;
                    }
                }
                if (slideIndex !== undefined) {
                    this.footerBarSlider.removeSlide(slideIndex);
                    this.footerBarSlider.update();
                }
            }
        });
    }

    /**
     * Add a card to the footer bar.
     * @param {HTMLElement} card - Card element to add.
     * @private
     */
    footerBarAddCard(card) {
        if (!this.footerBarCardsRepeater || !this.footerBarCardTemplate) {
            return;
        }

        // Get card data
        const cardDataset = card.dataset || {};
        const id = cardDataset.id ? Number(cardDataset.id) : 0;
        const title = cardDataset.title || '';

        if (!id) {
            return;
        }

        // Clone and prepare new card
        const clone = this.footerBarCardTemplate.content.cloneNode(true);
        const tempWrapper = document.createElement('div');
        tempWrapper.appendChild(clone);
        const newCard = tempWrapper.firstElementChild;
        if (!newCard) {
            return;
        }

        newCard.setAttribute('data-id', String(id));
        newCard.classList.add('swiper-slide');

        const titleEl = newCard.querySelector('.js-footer-card-title');
        if (titleEl) {
            titleEl.innerText = title;
        }

        // Lazy initialize Swiper *after* first slide is present
        if (!this.footerBarSlider) {
            // Append card to swiper-wrapper
            this.footerBarCardsRepeater.appendChild(newCard);

            this.initFooterBarSlider();
        } else {
            // Add slide dynamically
            this.footerBarSlider.appendSlide(newCard.outerHTML);
            this.footerBarSlider.update();
        }
    }

    /**
     * Initialize the footer bar slider.
     * @private
     */
    initFooterBarSlider() {
        if (!this.footerBarCardsContainer) {
            return;
        }

        this.footerBarSlider = new Swiper(this.footerBarCardsContainer, {
            slidesPerView: 2,
            spaceBetween: 8,
            observeSlideChildren: true,
            observer: true,
            centerInsufficientSlides: true,
            pagination: {
                el: this.footerBarCardsContainer.querySelector(
                    '.js-footer-pagination-progressbar',
                ),
                type: 'progressbar',
            },
            navigation: {
                nextEl: this.footerBarCardsContainer.querySelector(
                    '.js-footer-navigation .swiper-button-next',
                ),
                prevEl: this.footerBarCardsContainer.querySelector(
                    '.js-footer-navigation .swiper-button-prev',
                ),
            },
            breakpoints: {
                640: {
                    slidesPerView: 3,
                },
                768: {
                    slidesPerView: 4,
                },
            },
        });

        // Optional: delay update to allow layout
        setTimeout(() => {
            if (this.footerBarSlider) {
                this.footerBarSlider.update();
            }
        }, 0);
    }

    /**
     * Handle cancel button click in footer bar.
     * @private
     */
    footerBarCancelClickHandler() {
        const inputs = Array.from(
            this.cardsContainer.querySelectorAll(
                `input.js-table-input:checked`,
            ),
        );

        inputs.forEach((input) => {
            input.checked = false;
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
        });
    }

    /**
     * Handle compare plans button click.
     * @param {Event} event - Click event.
     * @private
     */
    comparePlansClickHandler(event) {
        event.preventDefault();

        this.prepareComparisonsCards();
        this.hideFooterBar();
        this.showComparisonsScreen();
    }

    /**
     * Create and prepare cards for comparison.
     * @private
     */
    prepareComparisonsCards() {
        if (!this.comparisonsScreenCardsRepeater) {
            return;
        }

        const swiperContainer =
            this.comparisonsScreenCardsRepeater.closest('.swiper-container');
        if (!swiperContainer) {
            return;
        }

        // Clear existing content and destroy previous slider
        this.comparisonsScreenCardsRepeater.innerHTML = '';
        if (
            this.comparisonsScreenSlider &&
            typeof this.comparisonsScreenSlider.destroy === 'function'
        ) {
            this.comparisonsScreenSlider.destroy(true, true);
            this.comparisonsScreenSlider = null;
        }

        const checkedCardsKeys = Object.keys(this.checkedCards);
        if (checkedCardsKeys.length) {
            // Clone and add each card to the comparison screen
            for (const id of checkedCardsKeys) {
                const card = this.checkedCards[id];
                if (!card) {
                    continue;
                }

                const clone = card.cloneNode(true);

                const overviewRows = clone.querySelector(
                    '.js-card-overview-rows',
                );
                const comparisonsRows = clone.querySelector(
                    '.js-card-comparison-rows',
                );
                const compareBlock = clone.querySelector('.js-card-compare');

                clone.classList.add('swiper-slide');
                clone.classList.remove('mix');
                clone.style.height = 'auto';

                if (overviewRows) {
                    overviewRows.classList.add('!hidden');
                }

                if (comparisonsRows) {
                    comparisonsRows.classList.remove('!hidden');
                }

                if (compareBlock) {
                    compareBlock.classList.add('!hidden');
                }

                this.comparisonsScreenCardsRepeater.appendChild(clone);
            }

            // Initialize comparison screen slider
            this.initComparisonScreenSlider(swiperContainer);
        }
    }

    /**
     * Initialize slider for comparison screen.
     * @param {HTMLElement} container - Swiper container element.
     * @private
     */
    initComparisonScreenSlider(container) {
        if (!container || !this.comparisonsScreen) {
            return;
        }

        this.comparisonsScreenSlider = new Swiper(container, {
            loop: false,
            autoplay: false,
            slidesPerView: 2,
            spaceBetween: 8,
            centerInsufficientSlides: true,
            mousewheel: false,
            pagination: {
                el: this.comparisonsScreen.querySelector(
                    '.js-cs-pagination-progressbar',
                ),
                type: 'progressbar',
            },
            navigation: {
                nextEl: this.comparisonsScreen.querySelector(
                    '.js-cs-navigation .swiper-button-next',
                ),
                prevEl: this.comparisonsScreen.querySelector(
                    '.js-cs-navigation .swiper-button-prev',
                ),
            },
            breakpoints: {
                768: {
                    slidesPerView: 3,
                },
            },
        });
    }

    /**
     * Handle clear button click in comparisons screen.
     * @param {Event} event - Click event.
     * @private
     */
    comparisonsClearButtonClickHandler(event) {
        event.preventDefault();

        const inputs = Array.from(
            this.cardsContainer.querySelectorAll(
                `input.js-table-input:checked`,
            ),
        );

        inputs.forEach((input) => {
            input.checked = false;
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
        });

        this.hideComparisonsScreen();
    }

    /**
     * Show comparison screen and update title.
     * @private
     */
    showComparisonsScreen() {
        if (!this.comparisonsScreen) {
            return;
        }

        const titleElement =
            this.comparisonsScreen.querySelector('.js-cs-title');
        if (titleElement) {
            let title = '';
            const checkedCards = Object.values(this.checkedCards);

            if (checkedCards.length) {
                for (let i = 0; i < checkedCards.length; i++) {
                    const cardDataset = checkedCards[i].dataset || {};
                    const cardTitle = cardDataset.title || '';

                    if (i === checkedCards.length - 1) {
                        title += ` and <span class="font-bold">${cardTitle}</span>`;
                    } else {
                        title += ` <span class="font-bold">${cardTitle}</span>,`;
                    }
                }
                title = `Comparing Plans${title}`;
            } else {
                title = '';
            }

            titleElement.innerHTML = title;
        }

        // Show comparison screen
        this.comparisonsScreen.classList.remove(
            'opacity-0',
            'invisible',
            'pointer-events-none',
        );

        // Scroll to comparison screen with header offset
        const header = document.querySelector('.js-header.sticky');
        const headerHeight = header ? header.offsetHeight : 0;
        const offsetPosition =
            window.scrollY +
            this.comparisonsScreen.getBoundingClientRect().top -
            headerHeight;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
        });

        // Adjust container height after transition
        setTimeout(() => {
            const wrapper = this.comparisonsScreen.querySelector(
                '.js-comparisons-screen-wrapper',
            );
            if (wrapper && this.container) {
                this.container.style.height = `${wrapper.offsetHeight}px`;
            }
        }, 400);
    }

    /**
     * Hide comparison screen.
     * @private
     */
    hideComparisonsScreen() {
        if (!this.comparisonsScreen) {
            return;
        }

        // Reset container height
        setTimeout(() => {
            if (this.container) {
                this.container.style.height = 'auto';
            }
        }, 200);

        this.comparisonsScreen.classList.add(
            'opacity-0',
            'invisible',
            'pointer-events-none',
        );
    }

    /**
     * Show footer bar.
     * @private
     */
    showFooterBar() {
        if (!this.footerBar) {
            return;
        }

        this.footerBar.classList.remove('translate-y-full');
    }

    /**
     * Hide footer bar and collapse it.
     * @private
     */
    hideFooterBar() {
        if (!this.footerBar) {
            return;
        }

        this.footerBar.classList.add('translate-y-full');

        setTimeout(() => {
            this.footerBarCollapse();
        }, 400);
    }

    /**
     * Handle collapse/expand button click in footer bar.
     * @param {Event} event - Click event.
     * @private
     */
    footerBarCollapseButtonClickHandler(event) {
        event.preventDefault();

        if (!this.footerBarCardsContainer) {
            return;
        }

        const isActive =
            this.footerBarCardsContainer.dataset &&
            this.footerBarCardsContainer.dataset.active === 'true';

        if (!isActive) {
            this.footerBarExpand();
        } else {
            this.footerBarCollapse();
        }
    }

    /**
     * Expand footer bar to show cards.
     * @private
     */
    footerBarExpand() {
        if (!this.footerBarCardsContainer || !this.footerBar) {
            return;
        }

        const footerBarIcon = this.footerBar.querySelector(
            '.js-footer-collapse-icon',
        );

        jQuery(this.footerBarCardsContainer).slideDown({
            start: function () {
                jQuery(this).css({
                    display: 'flex',
                });
            },
        });

        if (footerBarIcon) {
            footerBarIcon.classList.add('rotate-180');
        }

        if (this.footerBarCardsContainer.dataset) {
            this.footerBarCardsContainer.dataset.active = 'true';
        }
    }

    /**
     * Collapse footer bar to hide cards.
     * @private
     */
    footerBarCollapse() {
        if (!this.footerBarCardsContainer || !this.footerBar) {
            return;
        }

        const footerBarIcon = this.footerBar.querySelector(
            '.js-footer-collapse-icon',
        );

        jQuery(this.footerBarCardsContainer).slideUp();

        if (footerBarIcon) {
            footerBarIcon.classList.remove('rotate-180');
        }

        if (this.footerBarCardsContainer.dataset) {
            this.footerBarCardsContainer.dataset.active = 'false';
        }
    }
}

/**
 * Factory function to create a new Filter instance
 * @returns {Filter} A new Filter instance
 */
function filterA1() {
    return new Filter(document.querySelector(`[data-module='filterA1']`));
}

export default filterA1;
